import { type Permission, type Reservation, ReservationStatus } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { useMemo } from 'react';

import { createBookingCustomerRules, createCheckoutPermissions } from './use-checkout-permissions.hook';
import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createSpotcheckPermissions } from './use-spotcheck-permissions.hook';

interface FeatureFlags {
	allowPastBookings?: boolean;
}

type UseReservationPermissionsProps = PermissionsProps & FeatureFlags;

const MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST = { hours: 1 };

export const createReservationPermissions = (
	permissions: ReturnType<typeof usePermissions>,
	featureFlags?: FeatureFlags
) => {
	const { allowPastBookings = false } = featureFlags ?? {};
	const {
		hasAnyPermission: _hasAnyPermission,
		useReservations,
		useReservationsClose,
		useEquipmentPicking,
		usePastReservations,
		useBlockEditOfOpenReservations,
		usePdf,
		useSpotchecks,
		canUpgrade,
		useExport,
		useRepeatReservations,
	} = permissions;

	const checkoutPermissions = createCheckoutPermissions(permissions);
	const spotcheckPermissions = createSpotcheckPermissions(permissions);

	const hasAnyPermission = (...required: Permission[]) => useReservations && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('RESERVATIONS_WRITER', 'RESERVATIONS_WRITER_RESTRICTED') ||
		(own && hasAnyPermission('RESERVATIONS_OWN_WRITER'));

	/** Booking for somebody else (`setCustomer`), as python-api `create` decides it. */
	const bookForOthers = (): boolean =>
		!!hasAnyPermission('RESERVATIONS_WRITER_RESTRICTED') ||
		(!!hasAnyPermission('RESERVATIONS_WRITER') && !hasAnyPermission('RESERVATIONS_OWN_WRITER'));
	const bookingCustomerRules = createBookingCustomerRules(canWriter, bookForOthers, permissions);

	const canArchive = (own?: boolean) =>
		hasAnyPermission('RESERVATIONS_ARCHIVER', 'RESERVATIONS_ARCHIVER_RESTRICTED') ||
		(own && hasAnyPermission('RESERVATIONS_OWN_ARCHIVER'));

	const canLabel = (own?: boolean) =>
		hasAnyPermission('RESERVATIONS_LABELER', 'RESERVATIONS_LABELER_RESTRICTED') ||
		(own && hasAnyPermission('RESERVATIONS_OWN_LABELER'));

	const reservationPermissions = {
		// `own` must be passed for the own tier to count; no default.
		canRead: (own?: boolean) =>
			hasAnyPermission('RESERVATIONS_READER', 'RESERVATIONS_READER_RESTRICTED') ||
			(!!own && hasAnyPermission('RESERVATIONS_OWN_READER')),
		canCreate: () => canWriter(true),
		canReserve: canWriter,
		// Picking who a reservation is for (`setCustomer`): the own tier never covers somebody else.
		// python-api `create`: with RESERVATIONS_OWN_WRITER and no RESERVATIONS_WRITER_RESTRICTED, the booking is for yourself.
		canReserveForSomeoneElse: bookForOthers,
		canReserveFor: bookingCustomerRules.canBookFor,
		canChangeReservationUser: bookingCustomerRules.canChangeBookingUser,
		canUpdate: canWriter,
		canOverrideConflicts: () => hasAnyPermission('RESERVATIONS_CONFLICT_CREATOR'),
		canClose: (reservation?: Pick<Reservation, 'status'>, own?: boolean) =>
			!!useReservationsClose &&
			(!reservation || reservation.status === ReservationStatus.OPEN) &&
			(hasAnyPermission('RESERVATIONS_CLOSER', 'RESERVATIONS_CLOSER_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_CLOSER'))),
		canProceedToPickup: (reservation: Pick<Reservation, 'fromDate' | 'toDate' | 'status'>, own?: boolean) =>
			!!useEquipmentPicking && reservationPermissions.canConvertToCheckout(reservation, own),
		canConvertToCheckout: (reservation: Pick<Reservation, 'fromDate' | 'toDate' | 'status'>, own?: boolean) => {
			// python-api `makeOrder` needs a check-out writer right; `own` has no default.
			if (!checkoutPermissions.canUpdate(own)) return false;
			if (!reservation?.fromDate || !reservation.toDate) return false;
			if (reservation.status !== ReservationStatus.OPEN) return false;
			try {
				const now = DateTime.now();
				const isInPast =
					DateTime.fromISOString(reservation.fromDate).isBefore(now) &&
					DateTime.fromISOString(reservation.toDate).isBefore(now);
				return !isInPast;
			} catch (error) {
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.error('[canConvertToCheckout] DateTime operation failed:', error);
				}
				return false;
			}
		},
		canReadCheckouts: (own?: boolean) => checkoutPermissions.canRead(own),
		canWritePast: (own?: boolean) => {
			if (!usePastReservations || !allowPastBookings) return false;
			return hasAnyPermission('RESERVATIONS_WRITER_PAST') && canWriter(own);
		},
		canUndoFutureBooking: (reservation: Pick<Reservation, 'status'>, own?: boolean) =>
			reservation.status === ReservationStatus.OPEN && canWriter(own),
		canUndoPastBooking: (reservation: Pick<Reservation, 'status'>, own?: boolean) =>
			reservationPermissions.canUndoFutureBooking(reservation, own) && reservationPermissions.canWritePast(own),
		canUndoCurrentBooking: (reservation: Pick<Reservation, 'status' | 'fromDate'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.OPEN) return false;
			// Check if user has permission to undo bookings
			if (!canWriter(own)) return false;

			// If booking hasn't started yet (not in past), can always undo
			const isFromDateInPast =
				reservation.fromDate &&
				DateTime.fromISOString(reservation.fromDate).isBefore(
					DateTime.now().subtract(MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST)
				);
			if (!isFromDateInPast) {
				return true;
			}
			// For bookings that have already started (in past):
			// If past bookings are disabled OR blocking is disabled, always allow undo
			const usePastBookings = !!usePastReservations;
			const blockEditOfOpenBookings = !!useBlockEditOfOpenReservations;
			const alwaysAllowUndo = !usePastBookings || !blockEditOfOpenBookings;
			if (alwaysAllowUndo) {
				return true;
			}
			// If past bookings are enabled AND blocking is enabled:
			// Need write-past permission to undo past bookings
			return reservationPermissions.canWritePast(own);
		},
		canEdit: (reservation: Pick<Reservation, 'status'>, own?: boolean) =>
			reservation.status === ReservationStatus.OPEN && canWriter(own),
		canEditFields: (reservation: Pick<Reservation, 'archived'>, own?: boolean) =>
			!reservation.archived && canWriter(own),
		canDelete: (reservation: Pick<Reservation, 'status'>, own?: boolean) =>
			reservation.status === ReservationStatus.CREATING && canWriter(own),
		canCancel: (reservation: Pick<Reservation, 'status'>, own?: boolean) =>
			reservation.status === ReservationStatus.OPEN &&
			(hasAnyPermission('RESERVATIONS_CANCELER', 'RESERVATIONS_CANCELER_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_CANCELER'))),
		canBookAgain: canWriter,
		canRepeatBooking: (own?: boolean) => !!useRepeatReservations && !!canWriter(own),
		canArchive: (reservation: Pick<Reservation, 'status' | 'archived'>, own?: boolean) =>
			[ReservationStatus.CLOSED, ReservationStatus.CANCELLED].includes(reservation.status) &&
			!reservation.archived &&
			canArchive(own),
		canUndoArchive: (reservation: Pick<Reservation, 'archived'>, own?: boolean) =>
			!!reservation.archived && canArchive(own),
		// UI gate, as for check-outs and contacts: python-api `generateDocument` takes the own tier for any
		// reservation, but the UI offers it only on your own.
		canGenerateDocument: (own?: boolean) =>
			!!usePdf &&
			(hasAnyPermission('RESERVATIONS_DOCUMENT_GENERATOR', 'RESERVATIONS_DOCUMENT_GENERATOR_RESTRICTED') ||
				(!!own && hasAnyPermission('RESERVATIONS_OWN_DOCUMENT_GENERATOR'))),
		canUpgradePlan: canUpgrade,
		canCreateSpotcheck: () => !!useSpotchecks && spotcheckPermissions.canCreate(),
		canReadBooking: (own?: boolean) => reservationPermissions.canRead(own),
		canAddAttachment: (own?: boolean) =>
			hasAnyPermission('RESERVATIONS_ATTACHMENTS_OWN_WRITER') ||
			(!!own && hasAnyPermission('RESERVATIONS_OWN_ATTACHMENTS_OWN_WRITER')),
		/** `ownAttachment` / `ownComment` (it is yours) first, then `ownBooking`, as python-api and legacy. */
		canRemoveAttachment: (ownAttachment?: boolean, ownBooking?: boolean) =>
			hasAnyPermission('RESERVATIONS_ATTACHMENTS_DELETER') ||
			(!!ownAttachment && hasAnyPermission('RESERVATIONS_ATTACHMENTS_OWN_DELETER')) ||
			(!!ownAttachment && !!ownBooking && hasAnyPermission('RESERVATIONS_OWN_ATTACHMENTS_OWN_DELETER')),
		/** `ownBooking`: the reservation is the signed-in user's. */
		canAddComment: (ownBooking?: boolean) =>
			hasAnyPermission('RESERVATIONS_COMMENTS_OWN_WRITER') ||
			(!!ownBooking && hasAnyPermission('RESERVATIONS_OWN_COMMENTS_OWN_WRITER')),
		canEditComment: (ownComment?: boolean, ownBooking?: boolean) =>
			(!!ownComment && hasAnyPermission('RESERVATIONS_COMMENTS_OWN_WRITER')) ||
			(!!ownComment && !!ownBooking && hasAnyPermission('RESERVATIONS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownBooking?: boolean) =>
			hasAnyPermission('RESERVATIONS_COMMENTS_DELETER') ||
			(!!ownComment && hasAnyPermission('RESERVATIONS_COMMENTS_OWN_DELETER')) ||
			(!!ownComment && !!ownBooking && hasAnyPermission('RESERVATIONS_OWN_COMMENTS_OWN_DELETER')),
		canExport: () => !!useExport && hasAnyPermission('RESERVATIONS_EXPORTER', 'RESERVATIONS_EXPORTER_RESTRICTED'),
		canSetLabel: (reservation: Pick<Reservation, 'archived'>, own?: boolean) =>
			!reservation.archived && canLabel(own),
		canClearLabel: (reservation: Pick<Reservation, 'archived'>, own?: boolean) =>
			!reservation.archived && canLabel(own),
	};

	return reservationPermissions;
};

export const useReservationPermissions = ({ allowPastBookings = false, ...props }: UseReservationPermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(
		() => createReservationPermissions(permissions, { allowPastBookings }),
		[permissions, allowPastBookings]
	);
};
